import { useEffect, useRef } from 'react';
import { useSelectionStore } from './useSelection.js';
import { calculatePrice } from './usePrice.js';

/**
 * Sync the GF `.wsp-price-input` element with the calculated total whenever
 * the selection changes. The price input lives next to the tiles input inside
 * the GF field container; we locate it via the shared `data-gf-field-id`.
 *
 * No-op when no `.wsp-price-input` is present (e.g. inside the wsp_sponsor
 * meta-box, which only carries the tiles input).
 *
 * @param {string} fieldType
 * @param {Object} fieldConfig
 * @param {Array}  tiles
 */
export function usePriceInputSync( fieldType, fieldConfig, tiles ) {
	const inputRef = useRef( null );

	useEffect( () => {
		if ( ! fieldType ) return;
		const tilesEl = document.querySelector(
			`.wsp-tiles-input[data-field-type="${ fieldType }"]`
		);
		const gfFieldId = tilesEl?.dataset?.gfFieldId;
		if ( ! gfFieldId ) {
			inputRef.current = null;
			return;
		}
		inputRef.current = document.querySelector(
			`.wsp-price-input[data-gf-field-id="${ gfFieldId }"]`
		);
	}, [ fieldType ] );

	useEffect( () => {
		if ( ! fieldConfig || ! tiles?.length ) return;

		const writePrice = ( selected ) => {
			if ( ! inputRef.current ) return;
			const { total } = calculatePrice( selected ?? {}, fieldConfig, tiles );
			const totalStr = String( total );
			if ( inputRef.current.value !== totalStr ) {
				inputRef.current.value = totalStr;
				inputRef.current.dispatchEvent( new Event( 'change', { bubbles: true } ) );
			}
		};

		// Initial write (covers the case where selection was hydrated before mount).
		writePrice( useSelectionStore.getState().selected[ fieldType ] );

		const unsubscribe = useSelectionStore.subscribe(
			( state ) => {
				writePrice( state.selected[ fieldType ] );
			}
		);
		return unsubscribe;
	}, [ fieldType, fieldConfig, tiles ] );
}
